/* Eigenschaften, Schritt 2: Textur der Brushes, einzelne Fläche, Flächenwahl und Texturausrichtung. */
import { useState } from 'react';
import type { Brush, Doc, Sel } from '../../model/types.ts';
import Cmd from '../../model/cmds.ts';
import Core from '../../core.ts';
import Find from '../../model/find.ts';
import type { FaceRef } from '../../model/find.ts';
import TexAlign from '../../model/texalign.ts';
import type { Picked } from '../../model/texalign.ts';
import { usedTexNames } from '../../model/props.ts';
import { useT } from '../hooks.ts';
import { TexSelect } from '../assets/KindSelect.tsx';
import { CommitInput, NumCommit } from '../texture/widgets.tsx';

import type { AssetHost } from '../assets/host.ts';

export interface FaceApp {
  doc: Doc; sel: Sel; activeTex: string; faceSel: FaceRef[]; selFace: FaceRef | null;
  viewDoc(): Doc; wads: { size(n: string): { w: number; h: number } | null };
  v3: { setSel(): void } | null;
  status(msg: string): void; commit(doc: Doc, label: string): void;
  setActiveTex(name: string, apply?: boolean): void; applyTex(name: string): boolean; setFaceSel(list: FaceRef[]): void;
  /** Auswahlfelder für Texturen (fehlt in Tests) */
  assets?: AssetHost;
}
/** Zustand, der über das Neuzeichnen hinaus erhalten bleibt */
export interface FaceShared {
  faceScopeSel: boolean;
  align: { tx: number; ty: number; step: number; deg: number; withTex: boolean; pick: { brush: Brush; f: number; attrs: Picked } | null };
}

function findBrush(doc: Doc, id: string): Brush | null { for (const e of doc.ents) for (const b of e.brushes) if (b.id === id) return b; return null; }

const Btn = (p: { onClick: () => void; children: React.ReactNode; primary?: boolean; title?: string }) =>
  <button className={'btn sm' + (p.primary ? ' primary' : '')} title={p.title || ''} onClick={p.onClick}>{p.children}</button>;

export function FacePart(props: { app: FaceApp; shared: FaceShared; refresh: () => void }) {
  const t = useT();
  const { app, shared, refresh } = props, A = shared.align;
  const doc = app.viewDoc(), sel = app.sel, bs = Cmd.selBrushes(doc, sel);
  if (!bs.length) return null;
  const used = usedTexNames(bs);
  const sf = app.faceSel.length === 1 && app.selFace && sel.b.has(app.selFace.b) ? app.selFace : null;
  const multi = app.faceSel.length > 1;
  const fb = sf ? findBrush(doc, sf.b) : null;
  const face = fb && sf ? fb.faces[sf.f] : null;
  const faces: FaceRef[] | null = multi ? app.faceSel : (face && sf ? [sf] : null);
  const oneFace = faces && faces.length === 1 ? faces[0] : null;
  const deselect = () => { app.selFace = null; if (app.v3) app.v3.setSel(); refresh(); };
  return (
    <>
      <h3>{t('cmd.texture')}</h3>
      <p className="small">{t('pn.used', { list: used.slice(0, 5).join(', ') }) + (used.length > 5 ? ' … (' + used.length + ')' : '')}</p>
      <ApplyTex app={app} />
      {fb && face && sf ? (
        <SingleFace app={app} brush={fb} faceIdx={sf.f} onDeselect={deselect} />
      ) : multi ? (
        <>
          <h3>{t('ui.faces_selected', { count: app.faceSel.length })}</h3>
          <p className="small">{t('pn.multi_note')}</p>
          <div className="row"><Btn onClick={deselect}>{t('pn.deselect_faces')}</Btn></div>
        </>
      ) : <p className="small">{t('pn.single_face_hint')}</p>}
      <FaceTools app={app} shared={shared} />
      <Align app={app} shared={shared} bs={bs} faces={faces} one={oneFace} A={A} refresh={refresh} />
    </>
  );
}

/** Aktive Textur wählen und auf die Brushes anwenden; der getippte Text gilt auch ohne vorheriges Verlassen des Feldes */
function ApplyTex(props: { app: FaceApp }) {
  const t = useT();
  const { app } = props;
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? app.activeTex;
  return (
    <div className="row">
      {app.assets ? <TexSelect host={app.assets} label="active texture" browse={false} /> : (
        <input type="text" className="inp" list="dl-tex" autoComplete="off" spellCheck={false} aria-label="active texture" value={shown}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => { const v = shown.trim(); setText(null); if (v) app.setActiveTex(v, false); }} />
      )}
      <Btn primary onClick={() => { const v = shown.trim(); if (v) { app.activeTex = v; app.applyTex(v); setText(null); } }}>{t('pn.apply_brushes')}</Btn>
    </div>
  );
}

function SingleFace(props: { app: FaceApp; brush: Brush; faceIdx: number; onDeselect: () => void }) {
  const t = useT();
  const { app, brush, faceIdx } = props, f = brush.faces[faceIdx];
  const set = (patch: Record<string, unknown>, lbl?: string) => app.commit(Cmd.setFace(app.doc, brush.id, faceIdx, patch), lbl || t('cmd.face'));
  return (
    <>
      <h3>{t('pn.face_n', { n: faceIdx + 1, total: brush.faces.length })}</h3>
      <div className="fields">
        <label>{t('cmd.texture')}</label>
        {app.assets
          ? <TexSelect host={app.assets} label={t('cmd.texture')} value={f.tex} browse={false} onPick={(v) => { v = v.trim(); if (v) set({ tex: v }, t('cmd.face_tex')); }} />
          : <CommitInput list="dl-tex" aria-label={t('cmd.texture')} value={f.tex} onCommit={(v) => { v = v.trim(); if (v) set({ tex: v }, t('cmd.face_tex')); }} />}
        <label>{t('pn.offset_uv')}</label>
        <div className="xyz" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <NumCommit aria-label="uo" value={f.uo} onCommit={(v) => set({ uo: v }, t('pn.offset'))} />
          <NumCommit aria-label="vo" value={f.vo} onCommit={(v) => set({ vo: v }, t('pn.offset'))} />
        </div>
        <label>{t('pn.scale_uv')}</label>
        <div className="xyz" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <NumCommit aria-label="xs" value={f.xs} onCommit={(v) => { if (v > 0) set({ xs: v }, t('pn.scale')); }} />
          <NumCommit aria-label="ys" value={f.ys} onCommit={(v) => { if (v > 0) set({ ys: v }, t('pn.scale')); }} />
        </div>
      </div>
      <div className="row"><Btn onClick={props.onDeselect}>{t('pn.deselect_face')}</Btn></div>
    </>
  );
}

function FaceTools(props: { app: FaceApp; shared: FaceShared }) {
  const t = useT();
  const { app, shared } = props;
  const scope = () => (shared.faceScopeSel ? new Set(app.sel.b) : null);
  const go = (list: FaceRef[], what: string) => { if (!list.length) { app.status(t('pn.no_faces')); return; } app.setFaceSel(list); app.status(t('pn.faces_chosen', { count: list.length, what })); };
  const needRef = (fn: (r: FaceRef) => void) => () => { const ref = app.selFace && app.sel.b.has(app.selFace.b) ? app.selFace : null; if (!ref) { app.status(t('pn.pick_face_first')); return; } fn(ref); };
  return (
    <>
      <h3>{t('find.select_faces')}</h3>
      <div className="row">
        <Btn title={t('pn.all_of_brushes_tip')} onClick={() => go(Find.facesOf(app.doc, new Set(app.sel.b)), t('pn.what_all'))}>{t('pn.all_of_brushes')}</Btn>
        <Btn title={t('pn.same_dir_tip')} onClick={needRef((r) => go(Find.facesByNormal(app.viewDoc(), r, scope()), t('pn.what_dir')))}>{t('pn.same_dir')}</Btn>
        <Btn title={t('pn.same_tex_tip')} onClick={needRef((r) => {
          const b = findBrush(app.doc, r.b), tex = b && b.faces[r.f] ? b.faces[r.f].tex : null;
          if (!tex) return;
          go(Find.facesByTex(app.viewDoc(), tex, scope()).filter((x) => { const bb = findBrush(app.doc, x.b); return !!bb && bb.faces[x.f].tex.toUpperCase() === tex.toUpperCase(); }), t('pn.what_tex', { tex }));
        })}>{t('pn.same_tex')}</Btn>
      </div>
      <label className="chk"><input type="checkbox" defaultChecked={shared.faceScopeSel} onChange={(e) => { shared.faceScopeSel = e.target.checked; }} />{t('pn.scope_sel')}</label>
    </>
  );
}

type PatchFn = (b: Brush, i: number) => Record<string, unknown> | null;

function Align(props: { app: FaceApp; shared: FaceShared; bs: Brush[]; faces: FaceRef[] | null; one: FaceRef | null; A: FaceShared['align']; refresh: () => void }) {
  const t = useT();
  const { app, bs, faces, one: sf, A, refresh } = props;
  const run = (fn: PatchFn, label: string): boolean => {
    const d = TexAlign.apply(app.doc, app.sel.b, faces ? faces.map((x) => ({ b: x.b, f: x.f })) : null, fn as never);
    if (d === app.doc) { app.status(t('pn.nothing_to_change')); return false; }
    app.commit(d, label); return true;
  };
  const sz = (b: Brush, i: number) => app.wads.size(b.faces[i].tex) || { w: 64, h: 64 };
  const pk = A.pick;
  const L = (k: string, fn: PatchFn, label: string, title?: string) => <Btn title={title} onClick={() => run(fn, label)}>{k}</Btn>;
  const selectFace = (i: number) => { app.selFace = { b: bs[0].id, f: i }; if (app.v3) app.v3.setSel(); refresh(); };
  return (
    <>
      <h3>{t('pn.align')}</h3>
      {bs.length === 1 && (
        <div className="chips">
          {bs[0].faces.map((f, i) => (
            <button key={i} className={'chip' + (sf && sf.f === i ? ' act' : '')} title={f.tex} onClick={() => selectFace(i)}>{(i + 1) + ' ' + TexAlign.dirName(Core.faceNormal(bs[0].verts, f.vi))}</button>
          ))}
        </div>
      )}
      <p className="small">{faces ? t('pn.acts_on_faces', { count: faces.length }) : t('pn.acts_on_all')}</p>
      <div className="row">
        <span className="lbl-s">{t('pn.fit')}</span>
        <NumCommit width="56px" aria-label="fit x" value={A.tx} onCommit={(v) => { A.tx = Math.max(0.01, v); refresh(); }} />
        <span className="lbl-s">×</span>
        <NumCommit width="56px" aria-label="fit y" value={A.ty} onCommit={(v) => { A.ty = Math.max(0.01, v); refresh(); }} />
        <Btn primary title={t('pn.fit_tip')} onClick={() => run((b, i) => TexAlign.fit(b, i, A.tx, A.ty, sz(b, i).w, sz(b, i).h), t('cmd.tex_fit'))}>{t('pn.fit_btn')}</Btn>
      </div>
      <div className="row">
        <span className="lbl-s">{t('pn.rotate')}</span>
        {L('−90°', (b, i) => TexAlign.rotate(b, i, -90), t('cmd.tex_rotate'))}{L('+90°', (b, i) => TexAlign.rotate(b, i, 90), t('cmd.tex_rotate'))}
        <NumCommit width="56px" aria-label="rotate deg" value={A.deg} onCommit={(v) => { A.deg = v; refresh(); }} />
        {L('↺', (b, i) => TexAlign.rotate(b, i, A.deg), t('cmd.tex_rotate'), t('pn.rotate_tip'))}{L('↻', (b, i) => TexAlign.rotate(b, i, -A.deg), t('cmd.tex_rotate'))}
      </div>
      <div className="row">
        <span className="lbl-s">{t('cmd.mirror')}</span>
        {L('U', (b, i) => TexAlign.flipU(b, i), t('cmd.tex_mirror'))}{L('V', (b, i) => TexAlign.flipV(b, i), t('cmd.tex_mirror'))}
        <span className="lbl-s">{t('pn.scale')}</span>
        {L('×2', (b, i) => TexAlign.scale(b, i, 2), t('cmd.tex_scale'))}{L('÷2', (b, i) => TexAlign.scale(b, i, 0.5), t('cmd.tex_scale'))}
      </div>
      <div className="row">
        <span className="lbl-s">{t('pn.offset')}</span>
        <NumCommit width="56px" aria-label="offset step" value={A.step} onCommit={(v) => { A.step = v; refresh(); }} />
        {L('←', (b, i) => TexAlign.shift(b, i, -A.step, 0), t('cmd.tex_shift'))}{L('→', (b, i) => TexAlign.shift(b, i, A.step, 0), t('cmd.tex_shift'))}
        {L('↑', (b, i) => TexAlign.shift(b, i, 0, -A.step), t('cmd.tex_shift'))}{L('↓', (b, i) => TexAlign.shift(b, i, 0, A.step), t('cmd.tex_shift'))}
      </div>
      <div className="row">
        {L(t('pn.reset_world'), (b, i) => TexAlign.reset(b, i), t('cmd.tex_reset'), t('pn.reset_world_tip'))}
        {L(t('pn.corner0'), (b, i) => TexAlign.snapOrigin(b, i), t('cmd.tex_corner'), t('pn.corner0_tip'))}
      </div>
      <div className="row">
        <Btn title={t('pn.pick_tip')} onClick={() => {
          if (!sf) { app.status(t('pn.pick_single')); return; }
          const b = findBrush(app.doc, sf.b); if (!b) return;
          A.pick = { brush: b, f: sf.f, attrs: TexAlign.pick(b, sf.f) }; app.status(t('pn.picked', { tex: A.pick.attrs.tex })); refresh();
        }}>{t('pn.pick')}</Btn>
        <Btn title={t('pn.transfer_tip')} onClick={() => {
          if (!A.pick) return;
          const a = A.pick.attrs;
          run(() => { const r = Object.assign({}, a) as unknown as Record<string, unknown>; r.ua = a.ua.slice(); r.va = a.va.slice(); if (!A.withTex) delete r.tex; return r; }, t('cmd.tex_transfer'));
        }}>{t('pn.transfer')}</Btn>
        <label className="chk"><input type="checkbox" defaultChecked={A.withTex} onChange={(e) => { A.withTex = e.target.checked; }} />{t('pn.with_tex')}</label>
      </div>
      <div className="row">
        <Btn primary title={t('pn.continue_tip')} onClick={() => {
          if (!A.pick || !sf) { app.status(t('pn.continue_first')); return; }
          const tb = findBrush(app.doc, sf.b);
          const patch = tb ? TexAlign.continueOver(A.pick.brush, A.pick.f, tb, sf.f) as Record<string, unknown> | null : null;
          if (!patch) { app.status(t('pn.no_shared_edge')); return; }
          if (!A.withTex) delete patch.tex;
          const d = Core.mapBrushes(app.doc, new Set([sf.b]), (b: Brush) => Object.assign({}, b, { srcText: undefined, faces: b.faces.map((f, i) => (i === sf.f ? Object.assign({}, f, patch) : f)) }));
          app.commit(d, t('cmd.tex_continue'));
          const nb = findBrush(app.doc, sf.b); if (nb) A.pick = { brush: nb, f: sf.f, attrs: TexAlign.pick(nb, sf.f) };
          app.status(t('pn.continued')); refresh();
        }}>{t('pn.continue_edge')}</Btn>
      </div>
      <p className="small">{pk ? t('pn.picked_info', { tex: pk.attrs.tex, n: pk.f + 1 }) : t('pn.nothing_picked')}</p>
    </>
  );
}
